// 도메인 알림 통합 — 사건 마감 임박 / 메모 reminder / 정체 사건.
// 클라이언트 사이드 파생 (13-calendar-design.md §4.3 Phase 1 정책).
// 부모(상단바 Notification 컴포넌트)에서 호출 — 본인 데이터만 구독, 자동 unread 분류.

import { useEffect, useMemo, useState } from 'react';

import { useAuth } from 'contexts/AuthContext';
import { subscribeToAppraisals, type Appraisal } from 'firebase/appraisals-service';
import {
  subscribeToCalendarNotes,
  type CalendarNote
} from 'firebase/calendar-notes-service';
import {
  DEFAULT_NOTIFICATION_PREFS,
  deadlineLeadToDays,
  subscribeToCurrentUser,
  type AppUser,
  type NotificationPrefs
} from 'firebase/users-service';
import {
  STAGNANT_DAYS,
  computeStagnant
} from 'pages/dashboard/aggregations';
import { deriveEventsFromAppraisals } from 'utils/calendar-events';
import { getAppraisalDetailPath } from 'utils/appraisal-route';

export type NotificationKind = 'deadline' | 'memo' | 'stagnant';

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string; // 메인 메시지
  subtitle?: string; // 보조 (시간 / 단계 등)
  navigateTo: string;
  isUnread: boolean;
  sortKey: number; // 정렬용 ms (작을수록 위 — 시급한 순)
  colorKey: 'error' | 'warning' | 'info' | 'primary' | 'secondary';
}

const DAY_MS = 24 * 60 * 60 * 1000;

const dayDiff = (target: Date, base: Date = new Date()): number => {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  return Math.round((a.getTime() - b.getTime()) / DAY_MS);
};

interface ReturnShape {
  items: NotificationItem[];
  unreadCount: number;
  currentUser: AppUser | null;
  prefs: NotificationPrefs;
}

export function useNotifications(): ReturnShape {
  const { user, isFirebaseConfigured } = useAuth();
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [rows, setRows] = useState<Appraisal[]>([]);
  const [notes, setNotes] = useState<CalendarNote[]>([]);

  // 현재 사용자 (readUntil + prefs)
  useEffect(() => {
    if (!user || !isFirebaseConfigured) {
      setCurrentUser(null);
      return;
    }
    return subscribeToCurrentUser(user.uid, setCurrentUser);
  }, [user, isFirebaseConfigured]);

  // 사건
  useEffect(() => {
    if (!user || !isFirebaseConfigured) {
      setRows([]);
      return;
    }
    return subscribeToAppraisals(setRows);
  }, [user, isFirebaseConfigured]);

  // 메모 (본인)
  useEffect(() => {
    if (!user || !isFirebaseConfigured) {
      setNotes([]);
      return;
    }
    return subscribeToCalendarNotes(user.uid, setNotes);
  }, [user, isFirebaseConfigured]);

  // 옛 prefs (deadlineWarnings 등) 가 따라오지 않도록 새 필드만 명시적으로 추출.
  const fromDb = currentUser?.notificationPrefs;
  const prefs: NotificationPrefs = {
    deadlineLead: fromDb?.deadlineLead ?? DEFAULT_NOTIFICATION_PREFS.deadlineLead,
    stagnantEnabled: fromDb?.stagnantEnabled ?? DEFAULT_NOTIFICATION_PREFS.stagnantEnabled
  };
  const readUntilMs = currentUser?.notificationsReadUntil?.toMillis() ?? 0;

  const items = useMemo<NotificationItem[]>(() => {
    const now = new Date();
    const leadDays = deadlineLeadToDays(prefs.deadlineLead);
    const out: NotificationItem[] = [];

    // ─── 사건 마감 임박 ─────────────────────────────
    // 'OFF' (leadDays < 0) 면 마감 알림 자체 끄기.
    // 그 외엔 "지금 ~ leadDays 일 후" 사이 마감 매일 알림 (카운트다운).
    const derived = leadDays >= 0 ? deriveEventsFromAppraisals(rows) : [];
    for (const e of derived) {
      if (e.kind !== 'progress_due') continue;
      const d = new Date(e.date);
      if (Number.isNaN(d.getTime())) continue;
      const dday = dayDiff(d, now);
      if (dday < 0) continue; // 지난 마감은 알림 X
      if (dday > leadDays) continue; // 너무 먼 마감 — 시점 아직

      const appraisal = rows.find((r) => r.id === e.appraisalId);
      const dLabel = dday === 0 ? 'D-Day' : `D-${dday}`;
      const isCritical = dday <= 1;

      out.push({
        id: `deadline::${e.id}`,
        kind: 'deadline',
        title: `[${e.stageName ?? '진행단계'}] ${appraisal?.title ?? '(제목 없음)'}`,
        subtitle: `${dLabel} · ${e.date}`,
        navigateTo: getAppraisalDetailPath({
          id: e.appraisalId,
          is_assigned: appraisal?.is_assigned
        }),
        isUnread: d.getTime() > readUntilMs - DAY_MS, // 마감일 기준 unread
        sortKey: d.getTime(),
        colorKey: isCritical ? 'error' : 'warning'
      });
    }

    // ─── 메모 reminder ──────────────────────────────
    // 표시 기간: reminder 시점이 도래했거나 곧 (24h 안) + 메모 자체가 아직 안 끝남.
    //   메모 종료 = 시간 메모면 endAt(or startAt) / 종일 메모면 마지막 날 다음 자정.
    // unread 기준: 메모 생성 시점이 readUntil 이후 (생성 후 한 번도 안 본 메모).
    //   reminder 가 이미 지나도 사용자가 모르고 있을 수 있으므로 등록 직후엔 unread.
    //
    // ⚠️ 사무소 공유 정책 (2026-05-25): 본인 메모만 reminder 알림 (다른 사람이 30분 전 설정한 거를
    //    내가 받으면 어색). 다른 사람 사건 메모는 캘린더에서만 표시.
    const nowMs = now.getTime();
    const uid = user?.uid;
    for (const n of notes) {
      if (n.ownerId !== uid) continue;
      if (n.reminderMinutesBefore == null) continue;
      const reminderAt = n.startAt.toMillis() - n.reminderMinutesBefore * 60 * 1000;

      // 메모 종료 cutoff 계산
      let endCutoff: number;
      if (n.allDay) {
        const lastDay = n.endAt?.toDate() ?? n.startAt.toDate();
        const dayAfter = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1);
        endCutoff = dayAfter.getTime();
      } else {
        endCutoff = n.endAt?.toMillis() ?? n.startAt.toMillis();
      }

      if (reminderAt > nowMs + DAY_MS) continue; // 너무 먼 미래 — 24h 더 가야 reminder
      if (nowMs >= endCutoff) continue; // 메모 자체 종료됨

      // 메모 생성 시점 — 한 번이라도 읽었으면 (markRead 이후) not unread
      const createdMs =
        n.createdAt && typeof (n.createdAt as { toMillis?: () => number }).toMillis === 'function'
          ? (n.createdAt as { toMillis: () => number }).toMillis()
          : 0;

      out.push({
        id: `memo::${n.id}`,
        kind: 'memo',
        title: `[메모] ${n.title}`,
        subtitle: `${dayDiff(n.startAt.toDate(), now) === 0 ? '오늘' : `${dayDiff(n.startAt.toDate(), now)}일 후`}`,
        navigateTo: '/calendar',
        isUnread: createdMs > readUntilMs,
        sortKey: n.startAt.toMillis(), // 메모 시점 기준 정렬 (시급한 순)
        colorKey: 'info'
      });
    }

    // ─── 정체 사건 (옵션) ────────────────────────────
    if (prefs.stagnantEnabled) {
      const stagnant = computeStagnant(rows);
      // 알림은 상위 1건 묶음 — N건 통째로 (개별 표시는 대시보드에서)
      if (stagnant.length > 0) {
        out.push({
          id: `stagnant::summary`,
          kind: 'stagnant',
          title: `정체 사건 ${stagnant.length}건 확인 필요`,
          subtitle: `${STAGNANT_DAYS}일 이상 변경 없음`,
          navigateTo: '/dashboard/default',
          isUnread: true, // 정체 알림은 항상 표시 (read 처리 X — 사용자가 정리해야 사라짐)
          sortKey: Number.MAX_SAFE_INTEGER, // 항상 마지막
          colorKey: 'secondary'
        });
      }
    }

    // 시급한 순 (작은 sortKey 위), 정체는 마지막
    out.sort((a, b) => a.sortKey - b.sortKey);
    return out;
  }, [rows, notes, prefs, readUntilMs]);

  const unreadCount = items.filter((i) => i.isUnread).length;

  return { items, unreadCount, currentUser, prefs };
}
